import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  FaCheckCircle,
  FaHistory,
  FaInbox,
  FaSyncAlt,
  FaUtensils,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { SESSION_UPDATED_EVENT } from "../utils/accountLinks";
import { getStoredUser } from "../utils/impersonation";
import { API_BASE } from "../config/constants";
import { isFullOfflineModeEnabled } from "../utils/fullOfflineMode";
import { listOfflineKots, updateOfflineKotStatus } from "../offline/kotStore";
const DAY_MS = 1000 * 60 * 60 * 24;

const formatDateInput = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const readJson = async (response) => {
  const raw = await response.text();
  return raw ? JSON.parse(raw) : {};
};

const WaiterKOTPage = () => {
  const [user, setUser] = useState(() => getStoredUser() || {});
  const waiterId = useMemo(
    () => user?.staffid || user?.staff_profile?.id || null,
    [user]
  );
  const restaurantId = useMemo(
    () => user?.restaurantid || user?.restaurant_id || user?.restaurant_profile?.id || user?.id || null,
    [user]
  );
  const [activeTab, setActiveTab] = useState("status");
  const [fromDate, setFromDate] = useState(
    formatDateInput(new Date(Date.now() - 7 * DAY_MS))
  );
  const [toDate, setToDate] = useState(formatDateInput(new Date()));
  const [statusItems, setStatusItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  useEffect(() => {
    const refresh = () => setUser(getStoredUser() || {});
    window.addEventListener(SESSION_UPDATED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(SESSION_UPDATED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const loadStatus = useCallback(async () => {
    if (!waiterId) return;
    setStatusLoading(true);
    try {
      if (isFullOfflineModeEnabled()) {
        if (!restaurantId) throw new Error("Restaurant information is missing.");
        const result = await listOfflineKots(
          restaurantId,
          "prepared",
          fromDate,
          toDate,
          waiterId
        );
        setStatusItems(Array.isArray(result?.kot_items) ? result.kot_items : []);
        return;
      }
      const response = await fetch(`${API_BASE}/waiter/kot/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          waiterid: waiterId,
          type: "prepared",
          from_date: fromDate,
          to_date: toDate,
        }),
      });
      const result = await readJson(response);
      if (!response.ok) {
        throw new Error(result?.message || "Unable to load KOT status.");
      }
      setStatusItems(Array.isArray(result?.kot_items) ? result.kot_items : []);
    } catch (error) {
      toast.error(error?.message || "Unable to load KOT status.");
    } finally {
      setStatusLoading(false);
    }
  }, [fromDate, restaurantId, toDate, waiterId]);

  const loadHistory = useCallback(async () => {
    if (!waiterId) return;
    setHistoryLoading(true);
    try {
      if (isFullOfflineModeEnabled()) {
        if (!restaurantId) throw new Error("Restaurant information is missing.");
        const result = await listOfflineKots(
          restaurantId,
          "delivered",
          "",
          "",
          waiterId
        );
        setHistoryItems(Array.isArray(result?.kot_items) ? result.kot_items : []);
        return;
      }
      const response = await fetch(`${API_BASE}/waiter/kot/history`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ waiterid: waiterId }),
      });
      const result = await readJson(response);
      if (!response.ok) {
        throw new Error(result?.message || "Unable to load KOT history.");
      }
      const items = result?.delivered_items || result?.kot_items || [];
      setHistoryItems(Array.isArray(items) ? items : []);
    } catch (error) {
      toast.error(error?.message || "Unable to load KOT history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [restaurantId, waiterId]);

  const markDelivered = useCallback(
    async (kotItemId) => {
      if (!kotItemId) return;
      setMarkingId(kotItemId);
      try {
        if (isFullOfflineModeEnabled()) {
          if (!restaurantId) throw new Error("Restaurant information is missing.");
          const result = await updateOfflineKotStatus(restaurantId, kotItemId, "delivered");
          toast.success(result?.message || "Marked as delivered.");
          await Promise.all([loadStatus(), loadHistory()]);
          return;
        }
        const response = await fetch(`${API_BASE}/waiter/kot/mark-delivered`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ kotitem_id: kotItemId }),
        });
        const result = await readJson(response);
        if (!response.ok) {
          throw new Error(result?.error || result?.message || "Unable to update KOT.");
        }
        toast.success(result?.message || "Marked as delivered.");
        await Promise.all([loadStatus(), loadHistory()]);
      } catch (error) {
        toast.error(error?.message || "Unable to mark KOT as delivered.");
      } finally {
        setMarkingId(null);
      }
    },
    [loadHistory, loadStatus, restaurantId]
  );

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const emptyMessage =
    activeTab === "status"
      ? "No prepared KOT items found for the selected dates."
      : "No delivered KOT history found.";

  return (
    <Page>
      <Hero>
        <div>
          <HeroEyebrow>Waiter Operations</HeroEyebrow>
          <HeroTitle>My KOTs</HeroTitle>
          <HeroText>
            Track prepared items for delivery and review your delivered KOT history.
          </HeroText>
        </div>
      </Hero>

      {!waiterId ? (
        <EmptyState>
          <FaInbox size={28} />
          <h3>Waiter session not found</h3>
          <p>This login does not include a waiter staff ID required for KOT tracking.</p>
        </EmptyState>
      ) : (
        <>
          <Tabs>
            <TabButton
              type="button"
              $active={activeTab === "status"}
              onClick={() => setActiveTab("status")}
            >
              <FaUtensils />
              <span>Prepared</span>
            </TabButton>
            <TabButton
              type="button"
              $active={activeTab === "history"}
              onClick={() => setActiveTab("history")}
            >
              <FaHistory />
              <span>Delivered</span>
            </TabButton>
          </Tabs>

          {activeTab === "status" ? (
            <Panel>
              <FilterRow>
                <Field>
                  <label htmlFor="waiter-kot-from">From</label>
                  <input
                    id="waiter-kot-from"
                    type="date"
                    value={fromDate}
                    onChange={(event) => setFromDate(event.target.value)}
                  />
                </Field>
                <Field>
                  <label htmlFor="waiter-kot-to">To</label>
                  <input
                    id="waiter-kot-to"
                    type="date"
                    value={toDate}
                    onChange={(event) => setToDate(event.target.value)}
                  />
                </Field>
                <ActionButton type="button" onClick={loadStatus} disabled={statusLoading}>
                  <FaSyncAlt />
                  <span>{statusLoading ? "Refreshing..." : "Refresh"}</span>
                </ActionButton>
              </FilterRow>

              <CardGrid>
                {statusItems.map((item, index) => (
                  <KotCard key={String(item?.kotitem_id || item?.id || `status-${index}`)}>
                    <CardHead>
                      <strong>{item?.product_name || "Item"}</strong>
                      <StatusPill>Prepared</StatusPill>
                    </CardHead>
                    <CardMeta>
                      <span>Qty: {item?.qty || 0}</span>
                      <span>Table: {item?.tableno || "-"}</span>
                    </CardMeta>
                    {item?.kot_number ? (
                      <CardNote>KOT #{item.kot_number}</CardNote>
                    ) : null}
                    <PrimaryButton
                      type="button"
                      onClick={() => markDelivered(item?.kotitem_id)}
                      disabled={markingId === item?.kotitem_id}
                    >
                      <FaCheckCircle />
                      <span>
                        {markingId === item?.kotitem_id
                          ? "Updating..."
                          : "Mark Delivered"}
                      </span>
                    </PrimaryButton>
                  </KotCard>
                ))}
              </CardGrid>

              {!statusItems.length && !statusLoading ? (
                <EmptyState>
                  <FaInbox size={28} />
                  <h3>No active KOTs</h3>
                  <p>{emptyMessage}</p>
                </EmptyState>
              ) : null}
            </Panel>
          ) : (
            <Panel>
              <FilterRow>
                <ActionButton type="button" onClick={loadHistory} disabled={historyLoading}>
                  <FaSyncAlt />
                  <span>{historyLoading ? "Refreshing..." : "Refresh"}</span>
                </ActionButton>
              </FilterRow>

              <CardGrid>
                {historyItems.map((item, index) => (
                  <KotCard key={String(item?.kotitem_id || item?.id || `history-${index}`)}>
                    <CardHead>
                      <strong>{item?.product_name || "Item"}</strong>
                      <StatusPill $tone="success">Delivered</StatusPill>
                    </CardHead>
                    <CardMeta>
                      <span>Qty: {item?.qty || 0}</span>
                      <span>Table: {item?.tableno || "-"}</span>
                    </CardMeta>
                    {item?.kot_number ? (
                      <CardNote>KOT #{item.kot_number}</CardNote>
                    ) : null}
                    <DeliveredAt>
                      Delivered: {formatDateTime(item?.delivered_at || item?.delivered_time)}
                    </DeliveredAt>
                  </KotCard>
                ))}
              </CardGrid>

              {!historyItems.length && !historyLoading ? (
                <EmptyState>
                  <FaInbox size={28} />
                  <h3>No delivered items</h3>
                  <p>{emptyMessage}</p>
                </EmptyState>
              ) : null}
            </Panel>
          )}
        </>
      )}
    </Page>
  );
};

export default WaiterKOTPage;

const Page = styled.div`
  min-height: 100vh;
  padding: 24px;
  background:
    radial-gradient(circle at top right, rgba(16, 185, 129, 0.18), transparent 26%),
    linear-gradient(180deg, #f8fafc 0%, #eef6f8 100%);
  display: grid;
  gap: 18px;
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #0f172a 0%, #0f766e 100%);
  color: #f8fafc;
  padding: 24px;
  border-radius: 22px;
  box-shadow: 0 28px 50px rgba(15, 23, 42, 0.22);
`;

const HeroEyebrow = styled.div`
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.72;
`;

const HeroTitle = styled.h1`
  margin: 8px 0 6px;
  font-size: clamp(28px, 4vw, 36px);
  line-height: 1;
`;

const HeroText = styled.p`
  margin: 0;
  max-width: 680px;
  color: rgba(248, 250, 252, 0.86);
  line-height: 1.6;
`;

const Tabs = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  border: 0;
  border-radius: 999px;
  padding: 12px 16px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-weight: 700;
  color: ${({ $active }) => ($active ? "#f8fafc" : "#0f172a")};
  background: ${({ $active }) =>
    $active ? "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)" : "#ffffff"};
  box-shadow: ${({ $active }) =>
    $active ? "0 14px 28px rgba(15, 118, 110, 0.24)" : "0 10px 24px rgba(15, 23, 42, 0.06)"};
`;

const Panel = styled.section`
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 22px;
  padding: 20px;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.06);
  display: grid;
  gap: 18px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: end;
`;

const Field = styled.div`
  display: grid;
  gap: 6px;
  min-width: 180px;

  label {
    color: #475569;
    font-size: 13px;
    font-weight: 700;
  }

  input {
    height: 44px;
    border-radius: 12px;
    border: 1px solid rgba(148, 163, 184, 0.34);
    padding: 0 14px;
    font: inherit;
    color: #0f172a;
    background: #fff;
  }
`;

const ActionButton = styled.button`
  height: 44px;
  border: 0;
  border-radius: 12px;
  padding: 0 16px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  color: #f8fafc;
  background: linear-gradient(135deg, var(--app-primary) 0%, var(--app-primary) 100%);
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
`;

const CardGrid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const KotCard = styled.article`
  border-radius: 18px;
  padding: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 16px 28px rgba(15, 23, 42, 0.05);
  display: grid;
  gap: 12px;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  strong {
    color: #0f172a;
    font-size: 16px;
  }
`;

const StatusPill = styled.div`
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $tone }) => ($tone === "success" ? "#166534" : "#92400e")};
  background: ${({ $tone }) =>
    $tone === "success" ? "#dcfce7" : "#fef3c7"};
`;

const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
`;

const CardNote = styled.div`
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
`;

const PrimaryButton = styled.button`
  height: 42px;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 700;
  color: #f8fafc;
  background: linear-gradient(135deg, #15803d 0%, #22c55e 100%);
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
`;

const DeliveredAt = styled.div`
  color: #475569;
  font-size: 13px;
  font-weight: 600;
`;

const EmptyState = styled.div`
  min-height: 220px;
  border-radius: 20px;
  border: 1px dashed rgba(148, 163, 184, 0.4);
  background: rgba(255, 255, 255, 0.68);
  display: grid;
  place-items: center;
  text-align: center;
  gap: 8px;
  padding: 20px;
  color: #475569;

  h3 {
    margin: 0;
    color: #0f172a;
  }

  p {
    margin: 0;
    max-width: 420px;
    line-height: 1.5;
  }
`;
